import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Skeleton,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { formatPrice } from '../services/bookingService';
import ServiceGroupSelection from './ServiceGroupSelection';

// Info text for specific services
const SERVICE_INFO = {
  'hormone replacement therapy initial consultation': {
    title: 'About This Consultation',
    content: `This online call is with one of our staff, they will explain your hormone replacement therapy journey. After you complete an online form and upload recent blood test results, a doctor's consultation will be scheduled to discuss your treatment and make recommendations.

You'll then receive a link to a customer portal to enable you to order any of the recommended prescription (if appropriate) and non prescription products. Orders are shipped directly to you.

Blood tests are required every 12 weeks for doctor assessment if on a hormone program, follow up consultations with the doctor are $259.`
  },
  'longevity medicine (peptide therapy) consultation': {
    title: 'About This Consultation',
    content: `The initial online call is with one of our staff, they will explain your longevity medicine and peptide therapy journey. After you complete an online form, and speak to one of our staff, a doctor's consultation will be scheduled to discuss your treatment and make recommendations.

You'll receive a link to a customer portal to enable you to order any of the recommended prescription (if appropriate) and non prescription products.

Orders are shipped directly to you, from one of our Australian accredited partner pharmacies.

Important: After booking, please check your inbox for an email with a link to complete our Integrative Health Review Form.`
  },
  'weight loss consultation': {
    title: 'About This Consultation',
    content: `The initial online call is with one of our staff, they will explain your weight loss program and journey. After you complete an online form, and speak to one of our staff, a doctor's consultation will be scheduled to discuss your treatment and make recommendations.

You'll receive a link to a customer portal to enable you to order any of the recommended prescription (if appropriate) and non prescription products.

Orders are shipped directly to you, from one of our Australian accredited partner pharmacies.

Important: Our online intake form must be completed prior to your doctor consultation - this will be sent to you via email.`
  },
  'personalised nutrition plan': {
    title: 'About This Consultation',
    content: `The initial online call is with one of our staff, they will explain your weight loss program and journey. After you complete an online form, and speak to one of our staff, a doctor's consultation will be scheduled to discuss your treatment and make recommendations.

You'll receive a link to a customer portal to enable you to order any of the recommended prescription (if appropriate) and non prescription products.

Orders are shipped directly to you, from one of our Australian accredited partner pharmacies.

Important: Our online intake form must be completed prior to your doctor consultation - this will be sent to you via email.`
  },
  'integrative health plan': {
    title: 'About This Consultation',
    content: `One of our team will contact you to go through the process in detail.

Our online intake form must be completed prior to your doctor consultation - this will be sent to you via email.`
  }
};

// Helper function to check if a service should show info dialog
const getServiceInfo = (serviceName) => {
  if (!serviceName) return null;
  const normalizedName = serviceName.toLowerCase().trim();
  return SERVICE_INFO[normalizedName] || null;
};

const ServiceSelection = ({
  services,
  selectedService,
  onServiceSelect,
  loading = false,
  displayMode = 'flat',
  showInfoForServiceId = null  // If set, automatically show info dialog for this service
}) => {
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);
  const [pendingService, setPendingService] = useState(null);
  const [serviceInfo, setServiceInfo] = useState(null);
  const [autoShowTriggered, setAutoShowTriggered] = useState(false);

  // Auto-show info dialog when showInfoForServiceId is provided
  React.useEffect(() => {
    if (showInfoForServiceId && services && services.length > 0 && !autoShowTriggered) {
      const targetService = services.find(
        s => s.id.toString() === showInfoForServiceId.toString()
      );
      if (targetService) {
        const info = getServiceInfo(targetService.name);
        if (info) {
          // Show info dialog for this service
          setPendingService(targetService);
          setServiceInfo(info);
          setInfoDialogOpen(true);
          setAutoShowTriggered(true);
        } else {
          // No info dialog needed, select directly
          onServiceSelect(targetService);
          setAutoShowTriggered(true);
        }
      }
    }
  }, [showInfoForServiceId, services, autoShowTriggered, onServiceSelect]);

  // Handle service click - check if info dialog should be shown
  const handleServiceClick = (service) => {
    const info = getServiceInfo(service.name);
    if (info) {
      // Show info dialog before proceeding
      setPendingService(service);
      setServiceInfo(info);
      setInfoDialogOpen(true);
    } else {
      // No info dialog needed, proceed directly
      onServiceSelect(service);
    }
  };

  // Handle continue after reading info
  const handleContinue = () => {
    setInfoDialogOpen(false);
    if (pendingService) {
      onServiceSelect(pendingService);
      setPendingService(null);
      setServiceInfo(null);
    }
  };

  // Handle dialog close without continuing
  const handleCloseDialog = () => {
    setInfoDialogOpen(false);
    setPendingService(null);
    setServiceInfo(null);
  };
  console.log('ServiceSelection displayMode:', displayMode);
  
  // Use grouped display if displayMode is 'grouped'
  if (displayMode === 'grouped') {
    return (
      <>
        <ServiceGroupSelection
          services={services}
          selectedService={selectedService}
          onServiceSelect={handleServiceClick}
          loading={loading}
        />

        {/* Info Dialog for specific services */}
        <Dialog
          open={infoDialogOpen}
          onClose={handleCloseDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <InfoIcon color="primary" />
            {serviceInfo?.title || 'Important Information'}
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ whiteSpace: 'pre-line' }}>
              {serviceInfo?.content}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} color="inherit">
              Go Back
            </Button>
            <Button onClick={handleContinue} variant="contained" color="primary">
              I Understand, Continue
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }
  
  // Default flat display
  if (loading) {
    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          Select Service
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {[1, 2, 3, 4].map((item) => (
            <Card variant="outlined" key={item} sx={{ width: '100%', borderRadius: 1 }}>
              <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1 }}>
                <Box>
                  <Skeleton variant="text" width={180} height={28} />
                  <Skeleton variant="text" width={120} />
                </Box>
                <Skeleton variant="text" width={60} height={28} />
              </CardContent>
            </Card>
          ))}
        </Box>
      </Box>
    );
  }

  if (!services || services.length === 0) {
    return (
      <Alert severity="info">
        No services available for this clinic. Please contact the clinic directly.
      </Alert>
    );
  }

  // Filter to single service if showInfoForServiceId is provided
  const displayServices = showInfoForServiceId
    ? services.filter(s => s.id.toString() === showInfoForServiceId.toString())
    : services;

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {showInfoForServiceId ? 'Your Selected Service' : 'Select Service'}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {displayServices.map((service) => (
          <Card 
            key={service.id}
            variant="outlined"
            sx={{
              width: '100%',
              transition: 'box-shadow 0.2s',
              borderRadius: 1,
              ...(selectedService && selectedService.id === service.id ? {
                borderColor: 'primary.main',
                borderWidth: 2,
                boxShadow: 1
              } : {
                '&:hover': {
                  boxShadow: 1
                }
              })
            }}
          >
            <CardActionArea
              onClick={() => handleServiceClick(service)}
              sx={{ width: '100%' }}
            >
              <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1 }}>
                <Box>
                  <Typography variant="subtitle1" fontWeight="medium">
                    {service.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Duration: {service.duration_in_minutes} minutes
                  </Typography>
                </Box>
                <Typography variant="subtitle1" color="primary" fontWeight="bold">
                  {formatPrice(service.price)}
                </Typography>
              </CardContent>
            </CardActionArea>
          </Card>
        ))}
      </Box>

      {/* Info Dialog for specific services */}
      <Dialog
        open={infoDialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <InfoIcon color="primary" />
          {serviceInfo?.title || 'Important Information'}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ whiteSpace: 'pre-line' }}>
            {serviceInfo?.content}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="inherit">
            Go Back
          </Button>
          <Button onClick={handleContinue} variant="contained" color="primary">
            I Understand, Continue
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ServiceSelection;